import { IEnhancedApplePayPaymentRequest } from "../models/IEnhancedApplePayPaymentRequest";
import { IEnhancedApplePayShippingMethodChangedEvent } from "../models/IEnhancedApplePayShippingMethodChangedEvent";
import { ApplePayMockShippingContact } from "./ApplePayMockShippingContact";
import { ApplePayMockShippingMethod } from "./ApplePayMockShippingMethod";
export class ApplePayMockModal {
  private overlay: HTMLElement;
  private modalContent: HTMLElement;
  private totalAmount: string = "0.00";
  private price: string = "0.00";
  private paymentRequest: IEnhancedApplePayPaymentRequest;
  private shippingHandler: ApplePayMockShippingContact;
  private applePayMockShippingMethod: ApplePayMockShippingMethod;

  private onAuthorize: (contact?: ApplePayJS.ApplePayPayment) => void =
    () => {};
  private onCancel: () => void = () => {};
  private onCouponCodeChanged: (
    event: ApplePayJS.ApplePayCouponCodeChangedEvent,
  ) => void = () => {};

  constructor(paymentRequest: IEnhancedApplePayPaymentRequest) {
    this.paymentRequest = paymentRequest;
    this.applePayMockShippingMethod = new ApplePayMockShippingMethod(this);
    this.shippingHandler = new ApplePayMockShippingContact(this);

    this.overlay = document.createElement("div");
    this.modalContent = document.createElement("div");

    // Set IDs for easier CSS targeting and JS manipulation
    this.overlay.id = "applePayMockOverlay";
    this.modalContent.id = "applePayMockModal";

    this.setupOverlay();
    this.setupModal();
  }

  public setPaymentAuthorizedCallback(
    callback: (contact: ApplePayJS.ApplePayPayment) => void,
  ): void {
    this.onAuthorize = callback;
  }

  public setCancelCallback(callback: () => void): void {
    this.onCancel = callback;
  }

  public updateTotalAmount(totalAmount: string): void {
    this.totalAmount = totalAmount;
    if (this.modalContent) {
      this.updateContent();
    }
  }

  public updatePriceAndTotal(amount: string): void {
    this.price = amount;
    this.totalAmount = amount;
    if (this.modalContent) {
      this.updateContent();
    }
  }
  public setShippingMethodSelectedCallback(
    callback: (event: IEnhancedApplePayShippingMethodChangedEvent) => void,
  ): void {
    this.applePayMockShippingMethod.setShippingMethodCallback(callback);
  }

  private updateContent(): void {
    const priceElement = this.modalContent.querySelector("#price");
    const totalElement = this.modalContent.querySelector("#total-amount");
    if (priceElement) {
      priceElement.textContent = `Price: £${this.price}`;
    }
    if (totalElement) {
      totalElement.textContent = `Total: £${this.totalAmount}`;
    }
  }

  private setupOverlay(): void {
    Object.assign(this.overlay.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: "1000",
    });
  }

  public setShippingContactCallback(
    callback: (event: ApplePayJS.ApplePayShippingContactSelectedEvent) => void,
  ): void {
    this.shippingHandler.setShippingContactCallback(callback);
    this.shippingHandler.initShippingContactUI(
      this.paymentRequest.shippingContact,
      this.modalContent,
    );
    this.shippingHandler.onShippingContactSelected();
  }

  public setCouponCodeChangedCallback(
    callback: (event: ApplePayJS.ApplePayCouponCodeChangedEvent) => void,
  ): void {
    this.onCouponCodeChanged = callback;
  }

  public updateCouponCodeChanges(
    update: import("../models/IJwtEnhancedApplePayCouponCodeUpdate").IJwtEnhancedApplePayCouponCodeUpdate,
  ): void {
    if (update.newTotal) {
      this.updatePriceAndTotal(update.newTotal.amount);
    }
    if (update.errors && update.errors.length > 0) {
      this.showCouponError(update.errors[0].message);
    } else {
      this.hideCouponError();
    }
  }

  private setupModal(): void {
    Object.assign(this.modalContent.style, {
      maxWidth: "400px",
      minHeight: "600px",
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      padding: "20px",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      overflow: "auto",
    });
  }

  private invokeShippingMethod() {
    if (this.paymentRequest?.shippingMethods?.length > 0) {
      this.applePayMockShippingMethod.initShippingMethodUI(
        this.paymentRequest.shippingMethods,
        this.modalContent,
        this.price,
      );
    }
  }
  public open(): void {
    this.modalContent.innerHTML = this.createHtmlContent();
    this.overlay.appendChild(this.modalContent);
    document.body.appendChild(this.overlay);
    this.attachEventListeners();
    this.price = this.paymentRequest.total.amount;
    this.totalAmount = this.paymentRequest.total.amount;
    this.invokeShippingMethod();
    this.updateContent();
  }

  private attachEventListeners(): void {
    this.modalContent
      .querySelector("#cancel")
      ?.addEventListener("click", () => {
        this.close();
        this.onCancel();
      });

    this.modalContent
      .querySelector("#authorize")
      ?.addEventListener("click", () => {
        this.close();

        const contact = {
          shippingContact: this.shippingHandler.getSelectedAddress(),
          billingContact: this.paymentRequest.billingContact,
        };

        this.onAuthorize(contact as ApplePayJS.ApplePayPayment);
      });

    // Add coupon code apply button event listener
    const applyButton = this.modalContent.querySelector(
      "#apply-coupon",
    ) as HTMLButtonElement;
    if (applyButton) {
      applyButton.addEventListener("click", () => {
        const couponInput = this.modalContent.querySelector(
          "#coupon-code",
        ) as HTMLInputElement;
        const couponCode = couponInput?.value || "";
        this.onCouponCodeChanged({
          couponCode,
        } as ApplePayJS.ApplePayCouponCodeChangedEvent);
      });
    }
  }
  public close(): void {
    this.overlay.remove();
  }

  public unlock(): void {
    const authorizeButton = this.modalContent.querySelector(
      "#authorize",
    ) as HTMLButtonElement;
    if (authorizeButton) authorizeButton.disabled = false;
  }

  public updateShippingAmountChanges(
    status: unknown,
    newShippingMethods?: unknown,
    newTotal?: ApplePayJS.ApplePayLineItem,
    newLineItems?: ApplePayJS.ApplePayLineItem[],
  ): void {
    this.shippingHandler.handleShippingContactUpdate({
      status,
      newShippingMethods,
      newTotal,
      newLineItems,
    });
  }

  public updateShippingMethodAmountChanges(
    status: unknown,
    newTotal: unknown,
    newLineItems: unknown,
  ) {
    this.applePayMockShippingMethod.handleShippingMethodUpdate({
      status,
      newTotal,
      newLineItems,
    });
  }

  private showCouponError(message: string): void {
    const errorElement = this.modalContent.querySelector(
      ".coupon-error",
    ) as HTMLElement;
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = "block";
    }
  }

  private hideCouponError(): void {
    const errorElement = this.modalContent.querySelector(
      ".coupon-error",
    ) as HTMLElement;
    if (errorElement) {
      errorElement.style.display = "none";
    }
  }

  private createHtmlContent(): string {
    return `
      <style>
        .title {
          font-size: 24px;
          margin-bottom: 20px;
        }
        .order-summary {
          text-align: left;
          margin-bottom: 20px;
        }
        .order-summary h2 {
          font-size: 20px;
          margin: 0 0 10px 0;
        }
        .order-summary p {
          margin: 5px 0;
        }
        button {
          color: white;
          border: none;
          border-radius: 4px;
          padding: 10px 20px;
          margin: 10px 0;
          cursor: pointer;
          font-size: 16px;
          width: calc(100% - 20px);
        }
        button:hover {
          opacity: 0.8;
        }
        #cancel {
          background-color: #6185ab;
        }
      
        #authorize {
          background-color: #007aff;
        }
        .error-message {
          color: #ff3b30;
          display: none;
        }
        .coupon-section {
          margin: 15px 0;
        }
        .coupon-section input {
          width: calc(70% - 20px);
          padding: 5px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 16px;
          display: inline-block;
        }
        .coupon-section button {
          width: 25%;
          padding: 5px 10px;
          margin-left: 5px;
          background-color: #007aff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          display: inline-block;
        }
        .coupon-error {
          color: #ff3b30;
          font-size: 14px;
          margin-top: 5px;
          display: none;
        }
      </style>
      <div class="title">Payment</div>
      <div class="order-summary">
        <h2>Order Summary</h2>
        <p>Item: Fancy Widget</p>
        <p id="price">Price: £${this.price}</p>

         <div ><strong>Address:</strong><br></div>
          <p id="shipping-container"/>

        <div id="shipping-charges">Shipping Charges: Free</div>
        <p id="shippingmethod-container"></p>
        ${
          this.paymentRequest.supportsCouponCode
            ? `
          <div class="coupon-section">
            <label for="coupon-code">Coupon Code:</label><br/><br/>
            <input type="text" id="coupon-code" placeholder="Enter coupon code" value="${this.paymentRequest.couponCode || ""}">
            <button type="button" id="apply-coupon">Apply</button>
            <div class="coupon-error"></div>
          </div>
        `
            : ""
        }
        <p id="total-amount">Total: £${this.totalAmount}</p>
      </div>
      <button id="cancel">Cancel</button>
      <button id="authorize" disabled>Authorize</button>
    `;
  }
}
